/**
 * «Informe financiero» en PDF: un menú con las dos versiones (reunión del 22-10-2026).
 *
 *   · Corto: una o dos páginas, valores y bloques.
 *   · Largo («memoria»): portada, índice, resumen, resultados, balance, una ficha por ratio con sus
 *     umbrales y su porqué, posición fiscal y criterios.
 *
 * El periodo es el que tenga la pantalla (año, trimestre o mes). El PDF se genera siempre en español.
 */
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ChevronDown, FileBarChart, Loader2 } from 'lucide-react'
import { downloadFile } from '../../lib/download'

export type ReportPdfPeriod = 'YEAR' | 'Q1' | 'Q2' | 'Q3' | 'Q4' | 'MONTH'
type Variant = 'short' | 'long'

export default function ReportPdfMenu({
  year,
  period,
  month,
  notStarted = false,
  onError,
}: {
  year: number
  period: ReportPdfPeriod
  month: number
  /** El periodo elegido aún no ha empezado: no hay nada que informar. */
  notStarted?: boolean
  onError: (message: string) => void
}) {
  const { t } = useTranslation('accounting')
  const [open, setOpen] = useState(false)
  const [busy, setBusy] = useState<Variant | null>(null)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onDoc)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDoc)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  const download = async (variant: Variant) => {
    setBusy(variant)
    setOpen(false)
    try {
      const qs = new URLSearchParams({ year: String(year), period, variant })
      if (period === 'MONTH') qs.set('month', String(month))
      await downloadFile(
        `/accounting/kpis/pdf?${qs}`,
        `${variant === 'long' ? 'memoria-financiera' : 'informe-financiero'}-${year}.pdf`
      )
    } catch (err) {
      onError(err instanceof Error ? err.message : String(err))
    } finally {
      setBusy(null)
    }
  }

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        disabled={busy !== null || notStarted}
        title={notStarted ? t('reportPdf.notStarted') : undefined}
        aria-haspopup="menu"
        aria-expanded={open}
        className="px-2.5 py-1.5 rounded-md border border-base hover:bg-hover text-xs t-secondary flex items-center gap-1.5 disabled:opacity-60"
      >
        {busy ? <Loader2 size={14} className="animate-spin" /> : <FileBarChart size={14} />}
        {t('reportPdf.button')}
        <ChevronDown size={12} />
      </button>
      {open && (
        <div
          role="menu"
          className="absolute right-0 z-30 mt-1 w-72 rounded-lg border border-[var(--border)] bg-[var(--bg-panel,var(--bg-input))] p-1 shadow-xl"
        >
          {(['short', 'long'] as const).map((v) => (
            <button
              key={v}
              type="button"
              role="menuitem"
              onClick={() => void download(v)}
              className="w-full text-left rounded-md px-3 py-2 hover:bg-hover"
            >
              <div className="text-sm t-primary">{t(`reportPdf.${v}`)}</div>
              <div className="text-xs t-muted mt-0.5">{t(`reportPdf.${v}Hint`)}</div>
            </button>
          ))}
          <div className="px-3 pt-1 pb-2 text-[10px] t-faint">{t('reportPdf.note')}</div>
        </div>
      )}
    </div>
  )
}
